import { faBolt, faChartArea, faChartSimple, faClockRotateLeft, faComment, faCompass, faEye, faEyeSlash, faFilter, faHeart, faHistory, faLineChart, faList, faListCheck, faMap, faRightFromBracket, faRightLeft, faRobot, faRuler, faScrewdriverWrench, faSpinner, faUser, faUsersViewfinder, faComments, faCogs, faNoteSticky, faRocket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createRef, useState } from "react";
import { Badge, Button, ButtonGroup, Col, Container, Dropdown, DropdownButton, Form, Nav, Navbar, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { AsyncTypeahead, Highlighter, Menu } from "react-bootstrap-typeahead";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppAuthStateProps } from "../../types/propTypes";

import 'react-bootstrap-typeahead/css/Typeahead.css';
import { callQuery, FAVORITES_FOR_USERNAME_AND_TYPE } from "../../amplify/appsync/query";
import { callSearchPatients } from "../../amplify/appsync/search-patients";
import { isDemoUser } from '../../utils/demo';
import { getUserEmail, getUserRole, UserRole } from '../../utils/user';
import { useGlobalOptions } from '../common/GlobalOptionsContext';
import { AssumedRoleModal } from '../common/AssumedRoleModal';
import { getLocalDateTimeStr } from '../../utils/time';
import { UserAgreementModal } from '../common/UserAgreementModal';
import { TENANT_PHYSIOHUB } from '../../amplify/s3/storage';

interface ResponsiveAppBarProps extends AppAuthStateProps {
    assistantWidth: number;
}

export const ResponsiveAppBar = (props: ResponsiveAppBarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isPresentation, toggleIsPresentation, getDerivedRole, hasValidUserAgreement } = useGlobalOptions();

    const [isLoading, setIsLoading] = useState(false);
    const [showAssumedRoleModal, setShowAssumedRoleModal] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<any[]>([]);
    const [isLoadingFavorites, setIsLoadingFavorites] = useState(false);
    const [recents, setRecents] = useState<any[]>([]);
    const [isInputValid, setIsInputValid] = useState(true);
    const typeaheadRef = createRef<any>();

    const handleSearch = (query: string) => {
        // Validate input - only allow alphanumeric characters for CSN/MRN
        if (query && !/^[a-zA-Z0-9]+$/.test(query)) {
            setIsInputValid(false);
            setIsLoading(false);
            return;
        }

        setIsInputValid(true);
        setIsLoading(true);

        callSearchPatients({
            search: query,
        })
        .then((items: any[]) => {
            setOptions(items);
            setIsLoading(false);
        });
    };

    // Bypass client-side filtering by returning `true`. Results are already
    // filtered by the search endpoint, so no need to do it again.
    const filterBy = () => true;

    const handleFocusChange = () => {
        setIsLoadingFavorites(true);
        setIsOpen(true);

        callQuery({
            operation: FAVORITES_FOR_USERNAME_AND_TYPE,
            favorite_type: "recent",
            offset: 0,
            limit: 100,
        })
        .then((favorites: any[]) => {
            favorites = favorites.sort((a, b) => b.favorite_time.localeCompare(a.favorite_time));
            setRecents(favorites);
            setIsLoadingFavorites(false);
        });
    }

    const handleBlur = () => {
        setOptions([]);
        setIsOpen(false);
    }

    const handleSearchChange = (selected: any) => {
        const selectedInternalCSN = selected[0]["internal_csn"];
        navigateToInternalCsn(selectedInternalCSN);
    }

    const navigateToInternalCsn = (selectedInternalCSN: string) => {
        navigate(`/dashboard/${selectedInternalCSN}`);
        typeaheadRef.current.clear();
        setOptions([]);
        setIsOpen(false);
    }

    const renderTooltip = (props: any, tooltipName: string) => (
        <Tooltip {...props}>
            {tooltipName}
        </Tooltip>
    );

    const openFeedbackUrl = () => {
        let internalIdentifier = location.pathname;
        let internalIdentifierArr = internalIdentifier.split("/");
        internalIdentifier = internalIdentifierArr[internalIdentifierArr.length - 1];
        window.open(`https://docs.google.com/forms/d/e/1FAIpQLScIf6nuCmpWzkDD6oS-Nf7fWW80cditCHrYv9vLkLo53GZCIA/viewform?usp=pp_url&entry.532555191=${internalIdentifier}`, "_blank");
    };

    return (
        <>  
            <Navbar expand="lg" className={`responsive-app-bar ${props.assistantOpen ? 'assistant-layout-shifted' : ''}`} fixed="top" 
                style={{
                    marginRight: props.assistantOpen ? `${props.assistantWidth}px` : undefined
                }}
            >
                <Container fluid>
                    <Navbar.Brand href="/">
                        <img
                            alt="Capacity Health"
                            src={props.tenant === TENANT_PHYSIOHUB ? "/logo-stanford.png" : "/logo-capacity.svg"}
                            height="28"
                            className="d-inline-block align-top"
                        />{' '}
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="navbarScroll" />
                    <Navbar.Collapse id="navbarScroll">
                        <Nav
                            className="me-auto d-inline w-50"
                            navbarScroll
                        >
                            <AsyncTypeahead
                                ref={typeaheadRef}
                                filterBy={filterBy}
                                size="sm"
                                id="async-example"
                                isLoading={isLoading}
                                labelKey="csn"
                                minLength={3}
                                onSearch={handleSearch}
                                options={options}
                                style={{width: '100%'}}
                                onFocus={handleFocusChange}
                                onBlur={handleBlur}
                                disabled={isDemoUser(props.user)}
                                open={isOpen}
                                onChange={(e) => handleSearchChange(e)}
                                placeholder="Search for a patient visit by CSN or MRN..."
                                isInvalid={!isInputValid}
                                inputProps={{
                                    pattern: "[a-zA-Z0-9]*",
                                    title: "Please enter only letters and numbers",
                                    onPaste: (e) => {
                                        const pastedText = e.clipboardData.getData('text');
                                        const trimmedText = pastedText.trim();
                                        if (trimmedText.length >= 3) {
                                            // Small delay to let the input field update
                                            setTimeout(() => {
                                            handleSearch(trimmedText);
                                            }, 0);
                                        }
                                    },
                                    onInput: (e: any) => {
                                        const value = e.target.value;
                                        // Reset validation state when user starts typing
                                        if (!value || /^[a-zA-Z0-9]*$/.test(value)) {
                                            setIsInputValid(true);
                                        } else {
                                            setIsInputValid(false);
                                        }
                                    }
                                }}
                                renderMenu={(
                                    results,
                                    props,
                                    state
                                    ) => {
                                    return (
                                        <Menu {...props}>
                                            {options.map((option, idx) => {
                                                return (
                                                    <Row key={`search-${option.csn}`} className="search-dropdown-item" onClick={() => navigateToInternalCsn(option.internal_csn)}>
                                                        <Col md={1}>
                                                            <span className="me-2"><FontAwesomeIcon icon={faUser} /></span>
                                                        </Col>
                                                        <Col md={11}>
                                                            <Row>
                                                                <Col>
                                                                    MRN <Highlighter search={state.text}>{option.mrn}</Highlighter> &bull; CSN <Highlighter search={state.text}>{option.csn}</Highlighter>
                                                                </Col>
                                                            </Row>
                                                            <Row>
                                                                <Col className="text-muted">
                                                                    <small>
                                                                        {option.arrival_time ? getLocalDateTimeStr(option.arrival_time) : "-"} to {option.departure_time ? getLocalDateTimeStr(option.departure_time): "-"}
                                                                    </small>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>
                                                )
                                            })}
                                            {
                                                options.length === 0 && isLoadingFavorites ? (
                                                    <>
                                                        <Menu.Header>Recents</Menu.Header>
                                                        <Container>
                                                            <Row>
                                                                <Col>
                                                                    <small>
                                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                                    </small>
                                                                </Col>
                                                            </Row>
                                                        </Container>
                                                    </>
                                                ) : options.length === 0 && state.text && state.text.length >= 3 && !isLoading ? (
                                                    <>
                                                        <Container>
                                                            <Row>
                                                                <Col className="favorites-none">
                                                                    <small>No results found for "{state.text}"</small>
                                                                </Col>
                                                            </Row>
                                                        </Container>
                                                    </>
                                                ) : options.length === 0 ? (
                                                    <>
                                                        <Menu.Header>Recents</Menu.Header>
                                                        {
                                                            recents.length === 0 && (
                                                                <Container>
                                                                    <Row>
                                                                        <Col className="favorites-none">
                                                                            <small>No recents</small>
                                                                        </Col>
                                                                    </Row>
                                                                </Container>
                                                            )
                                                        }
                                                        {recents.map((option, idx) => {
                                                            return (
                                                                <Row key={`recent-${option.csn}`} className="search-dropdown-item" onClick={() => navigateToInternalCsn(option.internal_csn)}>
                                                                    <Col md={1}>
                                                                        <span className="me-2"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                                                                    </Col>
                                                                    <Col md={11}>
                                                                        <Row>
                                                                            <Col>
                                                                                MRN <Highlighter search={state.text}>{option.mrn}</Highlighter> &bull; CSN <Highlighter search={state.text}>{option.csn}</Highlighter>
                                                                            </Col>
                                                                        </Row>
                                                                        <Row>
                                                                            <Col className="text-muted">
                                                                                <small>
                                                                                    {option.arrival_time ? getLocalDateTimeStr(option.arrival_time) : "-"} to {option.departure_time ? getLocalDateTimeStr(option.departure_time) : "-"}
                                                                                </small>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>
                                                                </Row>
                                                            )
                                                        })}
                                                    </>
                                                ) : (<></>)
                                            }
                                        </Menu>
                                    )
                                }}
                            />
                        </Nav>
                        <Nav
                            className="me-auto d-inline w-50"
                        >
                            <OverlayTrigger
                                placement="bottom"
                                trigger={["hover", "focus"]}
                                overlay={(props: any) => renderTooltip(props, "Trackboard")}
                            >
                                <Link
                                    to="/trackboard-v2"
                                    style={{ marginLeft: '12px' }}
                                    className={`btn btn-sm ${window.location.pathname.startsWith("/trackboard-v2") ? "btn-secondary" : "btn-outline-secondary"}`}
                                >
                                    <FontAwesomeIcon icon={faList} />
                                </Link>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="bottom"
                                trigger={["hover", "focus"]}
                                overlay={(props: any) => renderTooltip(props, "Historic Search")}
                            >
                                <Link
                                    to="/search"
                                    style={{ marginLeft: '4px' }}
                                    className={`btn btn-sm ${window.location.pathname.startsWith("/search") ? "btn-secondary" : "btn-outline-secondary"}`}
                                >
                                    <FontAwesomeIcon icon={faFilter} />
                                </Link>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="bottom"
                                trigger={["hover", "focus"]}
                                overlay={(props: any) => renderTooltip(props, "Favorites")}
                            >
                                <Link
                                    to="/favorites"
                                    style={{ marginLeft: '4px' }}
                                    className={`btn btn-sm ${window.location.pathname.startsWith("/favorites") ? "btn-secondary" : "btn-outline-secondary"}`}
                                >
                                    <FontAwesomeIcon icon={faHeart} />
                                </Link>
                            </OverlayTrigger>
                            {
                                getDerivedRole(props.user) === UserRole.ADMIN && (
                                    <>
                                        {/* <Button style={{marginLeft: '4px'}} size="sm" variant="outline-secondary" onClick={() => navigate("cohort")}>
                                            <FontAwesomeIcon icon={faUsers} />
                                        </Button> */}
                                        <OverlayTrigger
                                            placement="bottom"
                                            trigger={["hover", "focus"]}
                                            overlay={(props: any) => renderTooltip(props, "Operations")}
                                        >
                                            <Link
                                                to="/ops"
                                                style={{ marginLeft: '4px' }}
                                                className={`btn btn-sm ${window.location.pathname.startsWith("/ops") ? "btn-secondary" : "btn-outline-secondary"}`}
                                            >
                                                <FontAwesomeIcon icon={faMap} />
                                            </Link>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="bottom"
                                            trigger={["hover", "focus"]}
                                            overlay={(props: any) => renderTooltip(props, "Reports")}
                                        >
                                            <ButtonGroup>
                                                  <DropdownButton
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    title={<FontAwesomeIcon icon={faChartArea} />}
                                                    align="end"
                                                    className="ms-1"
                                                >
                                                    <Dropdown.Item as={Link} to="/dashboard/admin">
                                                        <FontAwesomeIcon icon={faScrewdriverWrench} className='me-1' /> System Metrics
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/metrics">
                                                        <FontAwesomeIcon icon={faLineChart} className='me-1' /> Metrics
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/revisit">
                                                        <FontAwesomeIcon icon={faRightLeft} className='me-1' /> Revisit
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/boarding">
                                                        <FontAwesomeIcon icon={faHistory} className='me-1' /> Boarding
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/evaluation">
                                                        <FontAwesomeIcon icon={faRuler} className='me-1' /> Evaluation
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/llm-feedback">
                                                        <FontAwesomeIcon icon={faComment} className='me-1' /> LLM Feedback Report
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="https://logs.capacitybeta.com/">
                                                        <FontAwesomeIcon icon={faChartSimple} className='me-1' /> User Metrics
                                                    </Dropdown.Item>
                                                </DropdownButton>
                                            </ButtonGroup>
                                        </OverlayTrigger>
                                        <OverlayTrigger
                                            placement="bottom"
                                            trigger={["hover", "focus"]}
                                            overlay={(props: any) => renderTooltip(props, "Compass Study")}
                                        >

                                            <ButtonGroup>
                                                  <DropdownButton
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    title={<FontAwesomeIcon icon={faCompass} />}
                                                    align="end"
                                                    className="ms-1"
                                                >
                                                    <Dropdown.Item as={Link} to="/compass">
                                                        <FontAwesomeIcon icon={faListCheck} className='me-1' /> Compass Participants
                                                    </Dropdown.Item>
                                                    <Dropdown.Item as={Link} to="/trackboard">
                                                        <FontAwesomeIcon icon={faList} className='me-1' /> Compass Trackboard
                                                    </Dropdown.Item>
                                                </DropdownButton>
                                            </ButtonGroup>
                                        </OverlayTrigger>

                                        <OverlayTrigger
                                            placement="bottom"
                                            trigger={["hover", "focus"]}
                                            overlay={(props: any) => renderTooltip(props, "LIDIA Study")}
                                        >
                                            <Link
                                                to="/lidia-dashboard"
                                                className="btn btn-outline-secondary btn-sm ms-1"
                                            >
                                                <FontAwesomeIcon icon={faComments} />
                                            </Link>
                                        </OverlayTrigger>
                                    </>
                                )
                            }
                            <OverlayTrigger
                                placement="bottom"
                                trigger={["hover", "focus"]}
                                overlay={(props: any) => renderTooltip(props, "Add Feedback")}
                            >
                                <Button size="sm" variant="outline-secondary" className="ms-1" onClick={openFeedbackUrl}>
                                    <FontAwesomeIcon icon={faComment} />
                                </Button>
                            </OverlayTrigger>
                        </Nav>
                        <Form className="d-flex">
                            <div style={{marginTop: '6px', marginRight: '12px'}}>
                                {
                                    getUserRole(props.user) === UserRole.ADMIN && getDerivedRole(props.user) !== UserRole.ADMIN && <Badge bg="primary" onClick={() => setShowAssumedRoleModal(true)}>[Assumed] {getDerivedRole(props.user)}</Badge>
                                }
                            </div>
                            {
                                !props.assistantOpen && (
                                    <Button size="sm" variant="info" className="feedback-btn" style={{color: 'white'}} onClick={() => {props.setAssistantOpen(!props.assistantOpen)}}>
                                        <FontAwesomeIcon icon={faBolt} /> Assistant
                                    </Button>
                                )
                            }
                            <ButtonGroup>
                                <DropdownButton variant="link" title={
                                        <span className="avatar-circle">
                                            {getUserEmail(props.user)[0]}
                                        </span>
                                    }
                                    align="end"
                                >
                                    <Dropdown.Header>{getUserEmail(props.user)}</Dropdown.Header>
                                    <Dropdown.Divider />
                                    <Dropdown.Item as={Link} to="/profile">
                                        <FontAwesomeIcon icon={faCogs} size='xs' style={{marginRight: '8px'}} />{' '}Settings
                                    </Dropdown.Item>
                                    {
                                        getUserRole(props.user) === UserRole.ADMIN && (
                                            <Dropdown.Item onClick={() => {setShowAssumedRoleModal(true)}}>
                                                <FontAwesomeIcon icon={faUsersViewfinder} size='xs' style={{marginRight: '8px'}} />{' '}<>Assume Role</>
                                            </Dropdown.Item>
                                        )
                                    }
                                    <Dropdown.Item onClick={toggleIsPresentation}>
                                        <FontAwesomeIcon icon={isPresentation ? faEye : faEyeSlash} size='xs' style={{marginRight: '8px'}} />{' '}{isPresentation ? <>Show Identifiers</> : <>Hide Identifiers</>}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item as={Link} to="/release-notes">
                                        <FontAwesomeIcon icon={faRocket} size='xs' style={{marginRight: '8px'}} />{' '}What's New
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => props.signOut()}>
                                        <FontAwesomeIcon icon={faRightFromBracket} size='xs' style={{marginRight: '8px'}} />{' '}Logout
                                    </Dropdown.Item>
                                </DropdownButton>
                            </ButtonGroup>
                        </Form>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            <AssumedRoleModal showModal={showAssumedRoleModal} setShowModal={setShowAssumedRoleModal} user={props.user} />
            <UserAgreementModal user={props.user} />
        </>
    );
};
